import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import {
  User,
  GroupEntity,
  GroupEntityLabels,
  GroupManagementMapping,
} from '../../services/definitions.service';
import { DataModelService } from '../../services/data-model.service';
import { UserRightsManagementService } from '../../services/user-rights-management.service';
import {
  NotiBottomSheetComponent,
  NotiSheetData,
  NotiType,
} from '../../noti-bottom-sheet/noti-bottom-sheet.component';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

interface GroupOption {
  id: number;
  label: string;
}

interface MemberEntry {
  user: User;
  displayName: string;
}

@Component({
  selector: 'app-manage-user-groups-dialog',
  templateUrl: './manage-user-groups-dialog.component.html',
  styleUrls: ['./manage-user-groups-dialog.component.css'],
})
export class ManageUserGroupsDialogComponent implements OnInit {
  allUsers: User[] = [];
  memberEntries: MemberEntry[] = [];
  availableGroups: GroupOption[] = [];
  selectedGroupId: number | null = null;
  isLoadingUsers = true;

  /** Autocomplete for adding users */
  addUserControl = new FormControl();
  filteredUsers: Observable<User[]>;

  /** Set of user UIDs currently being saved */
  processingUids: Set<string> = new Set();

  constructor(
    public dialogRef: MatDialogRef<ManageUserGroupsDialogComponent>,
    private dataModel: DataModelService,
    private userRightsService: UserRightsManagementService,
    private bottomSheet: MatBottomSheet
  ) {}

  ngOnInit(): void {
    this.buildAvailableGroups();
    this.loadUsers();
    this.filteredUsers = this.addUserControl.valueChanges.pipe(
      startWith(''),
      map((value) => typeof value === 'string' ? value : ''),
      map((search) => this.filterNonMembers(search))
    );
  }

  /**
   * Builds the list of groups the current user is allowed to manage.
   * Admins can manage all groups. Others only get mapped groups.
   */
  private buildAvailableGroups(): void {
    var allGroupOptions: GroupOption[] = Object.keys(GroupEntity)
      .filter(function (key) { return isNaN(Number(key)); })
      .map(function (key) {
        var id = GroupEntity[key] as number;
        return {
          id: id,
          label: GroupEntityLabels[id] || key,
        };
      })
      .sort(function (a, b) { return a.id - b.id; });

    if (this.userRightsService.isPartOfAdminGroup()) {
      this.availableGroups = allGroupOptions;
      return;
    }

    var allowedGroupIds = new Set<number>();
    for (var i = 0; i < GroupManagementMapping.length; i++) {
      var mapping = GroupManagementMapping[i];
      if (this.userRightsService.isPartOfGroup(mapping.managerGroup.valueOf())) {
        mapping.manageableGroups.forEach(function (g) { allowedGroupIds.add(g.valueOf()); });
      }
    }

    this.availableGroups = allGroupOptions.filter(function (g) { return allowedGroupIds.has(g.id); });
  }

  async loadUsers(): Promise<void> {
    this.isLoadingUsers = true;
    try {
      var users = await this.dataModel.loadAllUsers(false, true);
      if (Array.isArray(users)) {
        this.allUsers = (users as User[]).sort(function (a, b) {
          return (a.personalData?.firstName || '').localeCompare(b.personalData?.firstName || '');
        });
      }
    } catch {
      this.allUsers = [];
    }
    this.isLoadingUsers = false;

    if (this.selectedGroupId != null) {
      this.buildMemberEntries();
    }
  }

  getUserDisplayName(user: User): string {
    if (!user) return 'Unbekannt';
    var first = user.personalData?.firstName || '';
    var last = user.personalData?.lastName || '';
    var full = (first + ' ' + last).trim();
    return full || user.displayName || user.email || 'Unbekannt';
  }

  onGroupSelected(): void {
    this.addUserControl.setValue('');
    this.buildMemberEntries();
  }

  private buildMemberEntries(): void {
    if (this.selectedGroupId == null) {
      this.memberEntries = [];
      return;
    }

    var self = this;
    this.memberEntries = this.allUsers
      .filter(function (user) {
        return (user.userGroups || []).indexOf(self.selectedGroupId) !== -1;
      })
      .map(function (user) {
        return {
          user: user,
          displayName: self.getUserDisplayName(user),
        };
      })
      .sort(function (a, b) {
        return a.displayName.localeCompare(b.displayName);
      });
  }

  /**
   * Filters users that are NOT in the selected group, matching the search text.
   */
  private filterNonMembers(search: string): User[] {
    if (this.selectedGroupId == null) {
      return [];
    }
    var groupId = this.selectedGroupId;
    var lowerSearch = (search || '').toLowerCase();
    var self = this;
    return this.allUsers.filter(function (user) {
      var groups = user.userGroups || [];
      if (groups.indexOf(groupId) !== -1) {
        return false;
      }
      if (!lowerSearch) {
        return true;
      }
      var name = self.getUserDisplayName(user).toLowerCase();
      var email = (user.email || '').toLowerCase();
      return name.indexOf(lowerSearch) !== -1 || email.indexOf(lowerSearch) !== -1;
    });
  }

  displayUserFn(user: User): string {
    if (!user) return '';
    return this.getUserDisplayName(user);
  }

  async onAddUser(user: User): Promise<void> {
    if (!user || this.selectedGroupId == null || this.processingUids.has(user.uid)) {
      return;
    }

    this.processingUids.add(user.uid);

    var currentGroups: number[] = user.userGroups || [];
    var updatedGroups = currentGroups.concat([this.selectedGroupId]);

    try {
      var success = await this.dataModel.updateUser(
        user.uid,
        { userGroups: updatedGroups },
        true
      );

      if (success) {
        user.userGroups = updatedGroups;
        this.buildMemberEntries();
        this.addUserControl.setValue('');
      } else {
        this.showError('Benutzer konnte nicht hinzugefügt werden.');
      }
    } catch {
      this.showError('Fehler beim Hinzufügen des Benutzers.');
    }

    this.processingUids.delete(user.uid);
  }

  async onRemoveUser(entry: MemberEntry): Promise<void> {
    if (this.selectedGroupId == null || this.processingUids.has(entry.user.uid)) {
      return;
    }

    var sheetRef = this.bottomSheet.open(NotiBottomSheetComponent, {
      data: new NotiSheetData(
        '"' + entry.displayName + '" wirklich aus der Gruppe entfernen?',
        NotiType.Warn,
        0,
        true,
        true,
        'Entfernen',
        'Abbrechen'
      ),
      panelClass: 'noti-bottom-sheet-info',
      disableClose: false,
    });

    var self = this;
    sheetRef.afterDismissed().subscribe(async function (confirmed: boolean) {
      if (!confirmed) {
        return;
      }

      self.processingUids.add(entry.user.uid);

      var currentGroups: number[] = entry.user.userGroups || [];
      var updatedGroups = currentGroups.filter(function (g) { return g !== self.selectedGroupId; });

      try {
        var success = await self.dataModel.updateUser(
          entry.user.uid,
          { userGroups: updatedGroups },
          true
        );

        if (success) {
          entry.user.userGroups = updatedGroups;
          self.buildMemberEntries();
        } else {
          self.showError('Benutzer konnte nicht entfernt werden.');
        }
      } catch {
        self.showError('Fehler beim Entfernen des Benutzers.');
      }

      self.processingUids.delete(entry.user.uid);
    });
  }

  getGroupLabel(id: number): string {
    var option = this.availableGroups.find(function (g) { return g.id === id; });
    return option ? option.label : 'Gruppe #' + id;
  }

  isUserProcessing(uid: string): boolean {
    return this.processingUids.has(uid);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private showError(message: string): void {
    this.bottomSheet.open(NotiBottomSheetComponent, {
      data: new NotiSheetData(message, NotiType.Error, 3000, false, false),
      panelClass: 'noti-bottom-sheet-info',
      disableClose: false,
    });
  }
}
